<template>
  <component-guard :requiredPermissions="['31.1']">
    <div class="modal fade" id="invoiceStatusModal" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <!-- Header -->
          <div class="modal-header">
            <h4 class="modal-title">Invoice Status</h4>

            <button type="button" class="close" data-dismiss="modal">
              <span>&times;</span>
            </button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <!-- LOG -->
            <div class="section-title">Log</div>

            <div class="table-responsive log-table">
              <table class="table mb-0">
                <thead>
                  <tr>
                    <th>TANGGAL</th>
                    <th>DIBUAT OLEH</th>
                    <th>STATUS</th>
                    <th style="text-align: left">KETERANGAN</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-if="logs.length === 0">
                    <td colspan="4" class="text-center">
                      Saat ini, belum terdapat catatan log yang telah dibuat.
                    </td>
                  </tr>
                  <tr v-for="(item, index) in logs" :key="index">
                    <td>{{ item.date }}</td>
                    <td>{{ item.createdBy }}</td>
                    <td>
                      <StatusBadge
                        v-if="item.status"
                        :statusText="item.statusText"
                        :statusClass="item.statusClass"
                      />
                    </td>
                    <td>{{ item.description }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <component-guard :requiredPermissions="['31.2']">
              <hr />

              <!-- UPDATE STATUS -->
              <div style="width: 40%">
                <InputRadio
                  :disabled="logs.length === 0 || disabledAction"
                  name="invoice-status"
                  label="Perbarui Status"
                  type="horizontal"
                  styleType="button"
                  :value="selectedStatus"
                  :options="[
                    { label: 'Warning', value: 'warning' },
                    { label: 'Eligible', value: 'eligible' },
                  ]"
                  @change="selectedStatus = $event"
                  :customClass="{ labelClass: 'm-0' }"
                />
                <small class="text-danger" v-if="$v.selectedStatus.$error">
                  Status wajib dipilih
                </small>
              </div>

              <!-- REASON -->
              <div class="form-group mt-3">
                <label style="font-weight: 600"> Alasan </label>

                <textarea
                  :disabled="logs.length === 0 || disabledAction"
                  v-model="reason"
                  class="form-control"
                  rows="4"
                  placeholder="Masukkan alasan perubahan status..."
                ></textarea>
                <small class="text-danger" v-if="$v.reason.$error">
                  Alasan wajib diisi
                </small>
              </div>
            </component-guard>
          </div>

          <!-- Footer -->
          <component-guard :requiredPermissions="['31.2']">
            <div class="modal-footer">
              <button class="btn btn-outline-secondary" data-dismiss="modal">
                Batal
              </button>

              <button
                class="btn btn-primary"
                :disabled="isSubmitting || logs.length === 0 || disabledAction"
                @click="saveStatus"
              >
                <span
                  v-if="isSubmitting"
                  class="spinner-border spinner-border-sm mr-2"
                ></span>

                Simpan Perubahan
              </button>
            </div>
          </component-guard>
        </div>
      </div>
    </div>
  </component-guard>
</template>

<script>
import { InputRadio } from "../../Input";
import StatusBadge from "./StatusBadge.vue";

import { mapGetters, mapActions } from "vuex";
import { required } from "vuelidate/lib/validators";
import { alert } from "@/utils";

import { ComponentGuard, permissionMixin } from "@/components/Permissions";

export default {
  name: "InvoiceLogAndStatus",
  components: {
    InputRadio,
    StatusBadge,
    ComponentGuard,
  },
  mixins: [permissionMixin],
  data() {
    return {
      isSubmitting: false,

      selectedStatus: "",

      reason: "",
    };
  },
  validations: {
    reason: {
      required,
    },
    selectedStatus: {
      required,
    },
  },
  computed: {
    ...mapGetters("rentAdjustment", {
      verificationLogs: "getVerificationLogs",
      transferUnitId: "getId",
      transferUnitStatus: "getStatus",
    }),
    logs() {
      const logsData = [];
      if (this.verificationLogs && this.verificationLogs.invoiceLogs) {
        this.verificationLogs.invoiceLogs.forEach((log) => {
          const statusClassMapping = {
            warning: "red",
            eligible: "green",
          };
          const statusTextMapping = {
            warning: "Warning",
            eligible: "Eligible",
          };
          logsData.push({
            date: new Date(log.createdAt).toLocaleString("id-ID", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }),
            createdBy: log.createdByName,
            status: log.status,
            statusClass: statusClassMapping[log.status],
            statusText: statusTextMapping[log.status],
            description: log.note || "-",
          });
        });
      }
      return logsData;
    },
    disabledAction() {
      const hasPermission = this.hasPermission({
        requiredPermissions: ["31.2"],
      });

      return !hasPermission || this.transferUnitStatus !== "verification";
    },
  },

  methods: {
    ...mapActions("rentAdjustment", {
      getVerificationStatus: "getVerificationStatus",
      getDetailTransferLessee: "getDetailTransferLessee",
    }),
    async saveStatus() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.isSubmitting = true;

      try {
        const payload = {
          ltId: this.transferUnitId,
          type: "invoice",
          status: this.selectedStatus,
          note: this.reason,
        };

        console.log("Payload:", payload);

        const response = await this.$http.post(
          process.env.TRANSFER_UNIT_UPDATE_VERIFICATION_STATUS,
          payload,
          {
            headers: {
              "Asani-Token": localStorage.getItem("jwt"),
            },
          }
        );

        if (response.data.status !== 200) {
          return alert().fire({
            icon: "error",
            title: "Gagal menyimpan perubahan",
            text:
              response.data.message ||
              "Terjadi kesalahan saat menyimpan perubahan",
          });
        }

        await alert().fire({
          icon: "success",
          title: "Berhasil menyimpan perubahan",
          text: response.data.message || "Perubahan status berhasil disimpan",
        });

        this.reason = "";
        this.selectedStatus = "";
        this.$v.$reset();
        await this.getVerificationStatus({
          transferUnitId: this.transferUnitId,
          type: "invoice",
        });
        await this.getDetailTransferLessee(this.transferUnitId);

        $("#invoiceStatusModal").modal("hide");
      } catch (error) {
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Terjadi kesalahan saat menyimpan perubahan";

        await alert().fire({
          icon: "error",
          title: "Gagal menyimpan perubahan",
          text: errorMessage,
        });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>
<style scoped>
.modal-content {
  border: none;
  border-radius: 16px;
}

.modal-header {
  padding: 24px;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  padding: 16px 24px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #374151;
}

.log-table {
  border: 1px solid #d9dee7;
  border-radius: 6px;
  max-height: 200px;
  overflow-y: auto;
}

.log-table table {
  margin: 0;
}

.log-table th {
  background: #f8fafc;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  border-top: none;
}

.log-table th:nth-child(4) {
  width: 40%;
}

thead th {
  position: sticky;
  top: 0;
  z-index: 1;
}

.log-table td {
  font-size: 14px;
  vertical-align: top;
}

.status-options {
  display: flex;
  gap: 12px;
}

.status-option {
  width: 160px;
  height: 42px;

  border: 1px solid #d1d5db;
  border-radius: 6px;

  display: flex;
  align-items: center;

  padding: 0 12px;

  cursor: pointer;

  margin-bottom: 0;
}

.status-option.active {
  border-color: #1677ff;
}

.status-option input {
  margin-right: 10px;
}

.form-control {
  resize: none;
  min-height: 90px;
}

.btn-primary {
  min-width: 140px;
}

.btn {
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
}

.btn:disabled {
  background-color: #d1d5dc;
  cursor: not-allowed;
  color: #ffffff;
  border: none;
}
</style>


// WEBPACK FOOTER //
// src/components/RentAdjustment/CustomComponent/InvoiceLogAndStatus.vue